import { useMemo } from 'react';
import type { SciChartSurface } from 'scichart';
import {
  ExportImageButton,
  type ExportButtonPlacement,
} from './export-image-button';
import { createSciChartExporter } from './scichart-exporter';
import type {
  OutputFormat,
  SciChartCaptureOptions,
  SciChartElementHooks,
} from './types';

interface SciChartExportImageButtonProps {
  surfaceRef: React.RefObject<SciChartSurface | null>;
  containerRef: React.RefObject<HTMLElement | null>;
  chartLabel: string;
  filenamePrefix: string;
  // Optional per-chart toggles (element list + scaling/visibility hooks).
  elementHooks?: SciChartElementHooks;
  // Pause/resume the chart's render loop around the capture (recommended for
  // shared-cache surfaces to avoid mid-export repaints).
  suspendUpdates?: () => void;
  resumeUpdates?: () => void;
  supportedFormats?: OutputFormat[];
  className?: string;
  captureOptions?: SciChartCaptureOptions;
  // Render a compact icon-only ghost trigger instead of the labeled button.
  iconOnly?: boolean;
  // Icon-only anchoring within the chart root (see ExportImageButton).
  placement?: ExportButtonPlacement;
  // Text drawn under the title in the image, wrapped between parts to fit.
  // Memoize it, since a new list builds a new exporter.
  subtitle?: string[];
}

// Bundles `createSciChartExporter` + `ExportImageButton` so SciChart callers
// don't repeat the same exporter useMemo at every call site.
export const SciChartExportImageButton = ({
  surfaceRef,
  containerRef,
  chartLabel,
  filenamePrefix,
  elementHooks,
  suspendUpdates,
  resumeUpdates,
  supportedFormats,
  className,
  captureOptions,
  iconOnly,
  placement,
  subtitle,
}: SciChartExportImageButtonProps) => {
  const exporter = useMemo(
    () =>
      createSciChartExporter({
        getSurface: () => surfaceRef.current,
        getContainer: () => containerRef.current,
        elementHooks,
        suspendUpdates,
        resumeUpdates,
        captureOptions,
        getSubtitle: () => subtitle ?? null,
      }),
    [
      surfaceRef,
      containerRef,
      elementHooks,
      suspendUpdates,
      resumeUpdates,
      captureOptions,
      subtitle,
    ]
  );

  return (
    <ExportImageButton
      exporter={exporter}
      chartLabel={chartLabel}
      filenamePrefix={filenamePrefix}
      supportedFormats={supportedFormats}
      className={className}
      iconOnly={iconOnly}
      placement={placement}
    />
  );
};
